import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { formatDate, formatINR } from "@/lib/constants";
import { CheckCircle2, PhoneCall, ArrowRight } from "lucide-react";
import { AddClientButton } from "@/components/AddClientDialog";

const STATUS_TONE = {
  New: "bg-blue-50 text-blue-700 border-blue-200",
  Contacted: "bg-amber-50 text-amber-700 border-amber-200",
  "Discovery Call Scheduled": "bg-purple-50 text-purple-700 border-purple-200",
  "Discovery Call Done": "bg-emerald-50 text-emerald-700 border-emerald-200",
  Converted: "bg-primary/10 text-primary border-primary/30",
  Dropped: "bg-gray-100 text-gray-500 border-gray-200",
};

export default function ProspectsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [callNotes, setCallNotes] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/prospects");
      setProspects(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markDone = async (p) => {
    try {
      // find its appointment (if any) and update it, else patch prospect directly
      await api.patch(`/prospects/${p.id}`, {
        status: "Discovery Call Done",
        call_outcome_notes: callNotes,
        proceed_to_stage_2: true,
      });
      toast.success("Marked as Discovery Call Done");
      setSelected(null);
      setCallNotes("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const convert = async (p) => {
    try {
      const { data } = await api.post(`/prospects/${p.id}/convert`, {});
      toast.success("Prospect converted to client");
      navigate(`/app/clients/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Conversion failed");
    }
  };

  const drop = async (p) => {
    try {
      await api.patch(`/prospects/${p.id}`, { status: "Dropped" });
      toast.success("Marked dropped");
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const canConvert = user.role === "Admin" || user.role === "Advisor";
  const canLogCall = user.role !== "ServiceRep";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline mb-1 text-muted-foreground">Stage 1 · Discovery</p>
          <h1 className="font-dashboard text-3xl font-bold tracking-tight">Prospects</h1>
          <p className="mt-1 text-sm text-muted-foreground">Onboarding submissions ready for a Discovery Call.</p>
        </div>
        {canConvert && <AddClientButton onCreated={() => load()} />}
      </div>

      <Card className="border-border bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Contact</th>
                {user.role !== "ServiceRep" && <th className="px-4 py-3">Surplus / mo</th>}
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="prospects-table-body">
              {loading ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : prospects.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-16 text-center text-muted-foreground">No prospects yet. Share the onboarding form link with visitors.</td></tr>
              ) : prospects.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-secondary/20">
                  <td className="px-4 py-4 font-medium" data-testid={`prospect-name-${p.id}`}>
                    <button
                      onClick={() => navigate(`/app/prospects/${p.id}`)}
                      className="text-left text-foreground hover:text-primary hover:underline"
                      data-testid={`open-prospect-${p.id}`}
                    >
                      {p.full_name}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-muted-foreground">
                    <div>{p.email}</div>
                    <div className="text-xs">{p.mobile}</div>
                  </td>
                  {user.role !== "ServiceRep" && (
                    <td className="px-4 py-4">{formatINR(p.surplus_available_to_invest)}</td>
                  )}
                  <td className="px-4 py-4">
                    <span className={"rounded-full border px-2.5 py-1 text-xs font-medium " + (STATUS_TONE[p.status] || "bg-gray-100 text-gray-700 border-gray-200")}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-xs text-muted-foreground">{formatDate(p.created_at)}</td>
                  <td className="px-4 py-4">
                    <div className="flex justify-end gap-2">
                      {canLogCall && p.status !== "Converted" && p.status !== "Dropped" && (
                        <Dialog open={selected?.id === p.id} onOpenChange={(o) => { if (!o) { setSelected(null); setCallNotes(""); } }}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" onClick={() => setSelected(p)} data-testid={`log-call-${p.id}`}>
                              <PhoneCall className="mr-1 h-3 w-3" /> Log call
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader><DialogTitle>Discovery Call outcome</DialogTitle></DialogHeader>
                            <div className="space-y-3 py-3">
                              <p className="text-sm text-muted-foreground">
                                Reminder: nothing is sold on this call. Record the outcome so the next stage begins cleanly.
                              </p>
                              <Label>Notes</Label>
                              <Textarea rows={5} value={callNotes} onChange={(e) => setCallNotes(e.target.value)} placeholder="What did you learn? Any red flags? Proceed to Stage 2?" data-testid="call-notes-input" />
                            </div>
                            <DialogFooter>
                              <Button variant="outline" onClick={() => drop(p)} data-testid="mark-dropped">Mark dropped</Button>
                              <Button onClick={() => markDone(p)} data-testid="mark-call-done">Proceed to Stage 2 <CheckCircle2 className="ml-1 h-4 w-4" /></Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                      )}
                      {canConvert && p.status === "Discovery Call Done" && (
                        <Button size="sm" onClick={() => convert(p)} data-testid={`convert-${p.id}`}>
                          Convert <ArrowRight className="ml-1 h-3 w-3" />
                        </Button>
                      )}
                      {p.converted_client_id && (
                        <Button size="sm" variant="ghost" onClick={() => navigate(`/app/clients/${p.converted_client_id}`)} data-testid={`open-client-${p.id}`}>
                          Open client
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
